/**
 * Fetch wrapper for the MIC backend.
 *
 * Every request:
 *   - targets `${VITE_API_URL}${path}`
 *   - sends cookies (`credentials: "include"`) so the `mic_session`
 *     cookie is attached on authenticated calls
 *   - sends JSON content-type when a body is provided
 *   - parses the response as JSON (or returns undefined for 204)
 *   - throws `ApiError` with the status + detail string on non-2xx
 *
 * The `ApiError` class exposes `status` so callers can distinguish
 * 401 (not signed in), 403 (wrong role), 404, 409 (conflict), etc.
 */

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;

  constructor(status: number, detail: string) {
    super(`API ${status}: ${detail}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

interface FetchOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { method = "GET", body, headers: extraHeaders, signal } = options;

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    ...(extraHeaders ?? {}),
  };

  const init: RequestInit = {
    method,
    credentials: "include",
    headers,
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  if (signal !== undefined) {
    init.signal = signal;
  }

  const response = await fetch(`${API_URL}${path}`, init);

  if (!response.ok) {
    let detail = response.statusText || `HTTP ${response.status}`;
    try {
      const errorBody = (await response.json()) as { detail?: unknown };
      if (typeof errorBody.detail === "string") {
        detail = errorBody.detail;
      }
    } catch {
      // response body wasn't JSON — fall back to statusText
    }
    throw new ApiError(response.status, detail);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export { API_URL };
